/**
 * Saved Orders Service
 * Manages customer saved orders for specific restaurants
 */

import { supabase } from './supabase';
import { logger } from './logger';

export const savedOrdersService = {
  /**
   * Get all saved orders for a customer
   * @param {string} customerId - Customer UUID
   * @returns {Promise<Array>} Array of saved orders
   */
  async getSavedOrdersForCustomer(customerId) {
    try {
      logger.debug('Getting saved orders for customer', { customerId });

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .select('*')
        .eq('customer_id', customerId)
        .order('last_used', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      logger.debug('Found saved orders', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      logger.error('Error getting saved orders', { error: error.message, customerId });
      throw error;
    }
  },

  /**
   * Get saved order(s) for a specific restaurant
   * @param {string} customerId - Customer UUID
   * @param {string} restaurantId - Yelp business ID or restaurant name
   * @returns {Promise<Array>} Array of saved orders for this restaurant
   */
  async getSavedOrdersByRestaurant(customerId, restaurantId) {
    try {
      logger.debug('Getting saved orders for restaurant', { customerId, restaurantId });

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .select('*')
        .eq('customer_id', customerId)
        .or(`restaurant_yelp_id.eq.${restaurantId},restaurant_name.eq.${restaurantId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      logger.debug('Found saved orders for restaurant', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      logger.error('Error getting saved orders for restaurant', {
        error: error.message,
        customerId,
        restaurantId
      });
      throw error;
    }
  },

  /**
   * Get a single saved order by ID
   * @param {string} savedOrderId - Saved order UUID
   * @returns {Promise<Object>} Saved order object
   */
  async getSavedOrderById(savedOrderId) {
    try {
      logger.debug('Getting saved order by ID', { savedOrderId });

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .select('*')
        .eq('id', savedOrderId)
        .single();

      if (error) throw error;

      logger.debug('Found saved order', { savedOrderId });
      return data;
    } catch (error) {
      logger.error('Error getting saved order by ID', { error: error.message, savedOrderId });
      throw error;
    }
  },

  /**
   * Create a new saved order
   * @param {Object} orderData - Order data
   * @param {string} orderData.customerId - Customer UUID
   * @param {string} orderData.restaurantYelpId - Yelp business ID (optional)
   * @param {string} orderData.restaurantName - Restaurant name
   * @param {string} orderData.restaurantAddress - Restaurant address (optional)
   * @param {string} orderData.restaurantPhone - Restaurant phone (optional)
   * @param {Object} orderData.restaurantData - Full restaurant data (optional)
   * @param {string} orderData.orderTemplateName - Template name (default: "Default Order")
   * @param {string} orderData.orderItems - Order details as text
   * @param {number} orderData.averageOrderSize - Average cost (optional)
   * @param {string} orderData.orderNotes - Additional notes (optional)
   * @returns {Promise<Object>} Created saved order
   */
  async createSavedOrder(orderData) {
    try {
      logger.debug('Creating saved order', {
        customerId: orderData.customerId,
        restaurantName: orderData.restaurantName
      });

      const insertData = {
        customer_id: orderData.customerId,
        restaurant_yelp_id: orderData.restaurantYelpId || null,
        restaurant_name: orderData.restaurantName,
        restaurant_address: orderData.restaurantAddress || null,
        restaurant_phone: orderData.restaurantPhone || null,
        restaurant_data: orderData.restaurantData || null,
        order_template_name: orderData.orderTemplateName || 'Default Order',
        order_items: orderData.orderItems,
        average_order_size: orderData.averageOrderSize || null,
        order_notes: orderData.orderNotes || null,
        times_used: 0
      };

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        // Check if error is due to max orders limit
        if (error.message && error.message.includes('Maximum of 3 saved orders')) {
          throw new Error('You can only save up to 3 orders per restaurant. Please delete an existing order first.');
        }
        throw error;
      }

      logger.debug('Saved order created successfully', { savedOrderId: data.id });
      return data;
    } catch (error) {
      logger.error('Error creating saved order', {
        error: error.message,
        customerId: orderData.customerId
      });
      throw error;
    }
  },

  /**
   * Update an existing saved order
   * @param {string} savedOrderId - Saved order UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated saved order
   */
  async updateSavedOrder(savedOrderId, updates) {
    try {
      logger.debug('Updating saved order', { savedOrderId, updates });

      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // Remove fields that shouldn't be updated directly
      delete updateData.id;
      delete updateData.customer_id;
      delete updateData.created_at;
      delete updateData.times_used;

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .update(updateData)
        .eq('id', savedOrderId)
        .select()
        .single();

      if (error) throw error;

      logger.debug('Saved order updated successfully', { savedOrderId });
      return data;
    } catch (error) {
      logger.error('Error updating saved order', { error: error.message, savedOrderId });
      throw error;
    }
  },

  /**
   * Delete a saved order
   * @param {string} savedOrderId - Saved order UUID
   * @returns {Promise<boolean>} Success status
   */
  async deleteSavedOrder(savedOrderId) {
    try {
      logger.debug('Deleting saved order', { savedOrderId });

      const { error } = await supabase
        .from('customer_restaurant_orders')
        .delete()
        .eq('id', savedOrderId);

      if (error) throw error;

      logger.debug('Saved order deleted successfully', { savedOrderId });
      return true;
    } catch (error) {
      logger.error('Error deleting saved order', { error: error.message, savedOrderId });
      throw error;
    }
  },

  /**
   * Increment the usage count and update last_used timestamp
   * @param {string} savedOrderId - Saved order UUID
   * @returns {Promise<Object>} Updated saved order
   */
  async incrementOrderUsage(savedOrderId) {
    try {
      logger.debug('Incrementing order usage', { savedOrderId });

      // First get current times_used value
      const { data: currentData, error: fetchError } = await supabase
        .from('customer_restaurant_orders')
        .select('times_used')
        .eq('id', savedOrderId)
        .single();

      if (fetchError) throw fetchError;

      const newTimesUsed = (currentData.times_used || 0) + 1;

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .update({
          times_used: newTimesUsed,
          last_used: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', savedOrderId)
        .select()
        .single();

      if (error) throw error;

      logger.debug('Order usage incremented', { savedOrderId, timesUsed: newTimesUsed });
      return data;
    } catch (error) {
      logger.error('Error incrementing order usage', { error: error.message, savedOrderId });
      // Don't throw - this is not critical
      return null;
    }
  },

  /**
   * Check if customer has reached max saved orders for a restaurant
   * @param {string} customerId - Customer UUID
   * @param {string} restaurantId - Yelp business ID or restaurant name
   * @returns {Promise<boolean>} True if at max (3 orders)
   */
  async hasMaxSavedOrders(customerId, restaurantId) {
    try {
      const orders = await this.getSavedOrdersByRestaurant(customerId, restaurantId);
      return orders.length >= 3;
    } catch (error) {
      logger.error('Error checking max saved orders', { error: error.message });
      return false;
    }
  },

  /**
   * Get most frequently used saved orders
   * @param {string} customerId - Customer UUID
   * @param {number} limit - Number of orders to return
   * @returns {Promise<Array>} Array of most used saved orders
   */
  async getMostUsedOrders(customerId, limit = 5) {
    try {
      logger.debug('Getting most used orders', { customerId, limit });

      const { data, error } = await supabase
        .from('customer_restaurant_orders')
        .select('*')
        .eq('customer_id', customerId)
        .order('times_used', { ascending: false })
        .limit(limit);

      if (error) throw error;

      logger.debug('Found most used orders', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      logger.error('Error getting most used orders', { error: error.message, customerId });
      throw error;
    }
  }
};

export default savedOrdersService;
